import { Pokemon, Move, calculate, Field, Side } from '@smogon/calc';
import { getGen } from './showdownHelpers';
import { NATURE_TABLE } from '../data/commonSets';

// ── Internal helpers ──────────────────────────────────────────────────────────

const EV_STEP = 4;
const EV_MAX  = 252;

function evRange() {
  const vals = [];
  for (let i = 0; i <= EV_MAX; i += EV_STEP) vals.push(i);
  return vals; // [0, 4, 8, ..., 252]
}

const EV_VALUES = evRange();

/**
 * Build a @smogon/calc Pokemon object.
 * baseStats: { hp, atk, def, spa, spd, spe } — always passed as overrides for accuracy.
 */
function buildPokemon(gen, showdownName, baseStats, config) {
  const opts = {
    level: config.level ?? 50,
    nature: config.nature || 'Hardy',
    evs: {
      hp:  config.evs?.hp  ?? 0,
      atk: config.evs?.atk ?? 0,
      def: config.evs?.def ?? 0,
      spa: config.evs?.spa ?? 0,
      spd: config.evs?.spd ?? 0,
      spe: config.evs?.spe ?? 0,
    },
    ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
  };

  if (config.item && config.item !== 'None') opts.item = config.item;
  if (config.ability)  opts.ability  = config.ability;
  if (config.teraType) opts.teraType = config.teraType;
  if (config.boosts)   opts.boosts   = config.boosts;
  if (config.isDynamaxed) opts.isDynamaxed = true;

  // Always provide base stat overrides for accuracy
  if (baseStats) {
    opts.overrides = {
      baseStats: {
        hp:  baseStats.hp,
        atk: baseStats.atk,
        def: baseStats.def,
        spa: baseStats.spa,
        spd: baseStats.spd,
        spe: baseStats.spe,
      },
    };
  }

  try {
    return new Pokemon(gen, showdownName, opts);
  } catch {
    // Fallback: use a generic placeholder name if species isn't recognised
    return new Pokemon(gen, 'Bulbasaur', opts);
  }
}

/**
 * Build a @smogon/calc Field from a condition config.
 * isDefendingOur: true  → the condition attacker's screens are on the defender side (our Pokémon defends)
 *                 false → our Pokémon attacks, screens are on the target's defender side
 */
function buildField(condConfig, isDefendingOur) {
  const screens = condConfig.screens || {};
  const attackerSide = new Side({
    isReflect: !isDefendingOur ? (screens.reflect ?? false) : false,
    isLightScreen: !isDefendingOur ? (screens.lightScreen ?? false) : false,
    isAuroraVeil: !isDefendingOur ? (screens.auroraVeil ?? false) : false,
  });
  const defenderSide = new Side({
    isReflect: isDefendingOur ? (screens.reflect ?? false) : false,
    isLightScreen: isDefendingOur ? (screens.lightScreen ?? false) : false,
    isAuroraVeil: isDefendingOur ? (screens.auroraVeil ?? false) : false,
  });
  return new Field({
    weather: condConfig.weather || undefined,
    terrain: condConfig.terrain || undefined,
    attackerSide,
    defenderSide,
  });
}

/**
 * Run @smogon/calc for one attacker vs defender scenario.
 * Returns { min, max, defenderHP } or null on error.
 */
function calcDamage(gen, attacker, defender, moveName, isSpread, field) {
  try {
    const moveOpts = {};
    if (isSpread) moveOpts.spread = true;
    const move = new Move(gen, moveName, moveOpts);
    const result = calculate(gen, attacker, defender, move, field);

    let damage = result.damage;
    // damage can be an array (16 rolls), a number, or a nested structure for multi-hit
    let minDmg, maxDmg;
    if (Array.isArray(damage)) {
      if (Array.isArray(damage[0])) {
        // Multi-hit: array of arrays; sum the minimums and maximums
        minDmg = damage.reduce((s, rolls) => s + Math.min(...rolls), 0);
        maxDmg = damage.reduce((s, rolls) => s + Math.max(...rolls), 0);
      } else {
        minDmg = Math.min(...damage);
        maxDmg = Math.max(...damage);
      }
    } else {
      minDmg = maxDmg = damage;
    }

    return {
      min: minDmg,
      max: maxDmg,
      defenderHP: defender.maxHP(),
    };
  } catch {
    return null;
  }
}

// ── Core check functions ──────────────────────────────────────────────────────

/**
 * Check whether a SURVIVE condition passes for our Pokémon with given EVs.
 * The attacker uses the condition's preset config and base stats.
 */
function checkSurvive(gen, myShowdownName, myBaseStats, myConfig, myEvs, condition) {
  const gen9 = gen;
  const myPoke = buildPokemon(gen9, myShowdownName, myBaseStats, { ...myConfig, evs: myEvs });
  const attacker = buildPokemon(
    gen9,
    condition.targetShowdownName,
    condition.targetBaseStats,
    condition.targetConfig
  );
  const field = buildField(condition.targetConfig, true);
  const result = calcDamage(gen9, attacker, myPoke, condition.moveName, condition.targetConfig.isSpread, field);
  if (!result) return null;
  return {
    // Strictly less-than: maxDmg == maxHP means the Pokémon faints (0 HP left)
    passes: result.max < result.defenderHP,
    damageRange: [result.min, result.max],
    defenderHP: result.defenderHP,
  };
}

/**
 * Check whether a KO condition passes for our Pokémon with given EVs.
 * koTier: 1 = OHKO, 2 = 2HKO, 3 = 3HKO
 */
function checkKo(gen, myShowdownName, myBaseStats, myConfig, myEvs, condition) {
  const koTier = condition.koTier ?? 1;
  const myPoke = buildPokemon(gen, myShowdownName, myBaseStats, { ...myConfig, evs: myEvs });
  const defender = buildPokemon(
    gen,
    condition.targetShowdownName,
    condition.targetBaseStats,
    condition.targetConfig
  );
  const field = buildField(condition.targetConfig, false);
  const result = calcDamage(gen, myPoke, defender, condition.moveName, condition.targetConfig.isSpread, field);
  if (!result) return null;
  return {
    passes: result.min * koTier >= result.defenderHP,
    damageRange: [result.min, result.max],
    defenderHP: result.defenderHP,
    koTier,
  };
}

// ── Minimum EV finders ────────────────────────────────────────────────────────

/** Returns nature names that boost the given smogon stat key. */
function naturesBoostingStat(smogStatKey) {
  return Object.entries(NATURE_TABLE)
    .filter(([, v]) => v.boost === smogStatKey)
    .map(([name]) => name);
}

/**
 * Find the minimum EVs in the relevant offensive stat (atk or spa) so that
 * our Pokémon achieves a koTier KO.
 * Also tries beneficial natures; returns the combo that needs fewest EVs.
 *
 * Returns { stat: 'atk'|'spa', evs: number, nature: string } or null if impossible at 252.
 */
function findMinKoEvs(gen, myShowdownName, myBaseStats, myConfig, currentEvs, condition) {
  const moveData = condition.moveData;
  const offStat = (moveData?.category === 'Physical') ? 'atk' : 'spa';
  const smogOffKey = offStat === 'atk' ? 'attack' : 'specialAttack';

  const currentNature = myConfig.nature || 'Hardy';
  const naturesToTry = [currentNature,
    ...naturesBoostingStat(smogOffKey).filter(n => n !== currentNature)];

  let best = null; // { stat, evs, nature, evCost }

  for (const nature of naturesToTry) {
    const cfg = { ...myConfig, nature };
    for (let i = 0; i < EV_VALUES.length; i++) {
      const testEvs = { ...currentEvs, [offStat]: EV_VALUES[i] };
      const result = checkKo(gen, myShowdownName, myBaseStats, cfg, testEvs, condition);
      if (result?.passes) {
        const evCost = EV_VALUES[i];
        // Prefer fewer EVs; tiebreak: prefer current nature
        if (!best || evCost < best.evCost || (evCost === best.evCost && nature === currentNature)) {
          best = { stat: offStat, evs: EV_VALUES[i], nature, evCost };
        }
        break; // found minimum EVs for this nature; move to next nature
      }
    }
  }

  return best ? { stat: best.stat, evs: best.evs, nature: best.nature } : null;
}

/**
 * Find the minimum HP + defensive stat EVs so that our Pokémon survives the attack.
 * Also tries beneficial natures; picks the combo with the fewest total EVs.
 *
 * Returns { hp, defStat, defEvs, totalAdditional, nature } or null if impossible.
 */
function findMinSurviveEvs(gen, myShowdownName, myBaseStats, myConfig, currentEvs, condition) {
  const moveData = condition.moveData;
  const defStat = (moveData?.category === 'Physical') ? 'def' : 'spd';

  // Handle Psyshock / Psystrike / Secret Sword — Special attack targeting Def
  const psyshockMoves = new Set(['Psyshock', 'Psystrike', 'Secret Sword']);
  const effectiveDefStat = psyshockMoves.has(condition.moveName) ? 'def' : defStat;
  const smogDefKey = effectiveDefStat === 'def' ? 'defense' : 'specialDefense';

  const currentNature = myConfig.nature || 'Hardy';
  const naturesToTry = [currentNature,
    ...naturesBoostingStat(smogDefKey).filter(n => n !== currentNature)];

  let best = null; // { hp, defStat, defEvs, nature, evCost }

  for (const nature of naturesToTry) {
    const cfg = { ...myConfig, nature };

    // Outer loop: Def EVs; inner loop: HP EVs.
    // For each Def level find min HP, then compare all (Def, HP) pairs to pick lowest total.
    for (let di = 0; di < EV_VALUES.length; di++) {
      const defEv = EV_VALUES[di];
      for (let hi = 0; hi < EV_VALUES.length; hi++) {
        const hpEv = EV_VALUES[hi];
        const testEvs = { ...currentEvs, hp: hpEv, [effectiveDefStat]: defEv };
        const result = checkSurvive(gen, myShowdownName, myBaseStats, cfg, testEvs, condition);
        if (result?.passes) {
          const evCost = hpEv + defEv;
          // Prefer fewer total EVs; tiebreak: prefer current nature
          if (!best || evCost < best.evCost || (evCost === best.evCost && nature === currentNature)) {
            best = { hp: hpEv, defStat: effectiveDefStat, defEvs: defEv, nature, evCost };
          }
          break; // found minimum HP for this defEv + nature; move to next defEv
        }
      }
    }
  }

  return best ? {
    hp: best.hp,
    defStat: best.defStat,
    defEvs: best.defEvs,
    totalAdditional: best.evCost,
    nature: best.nature,
  } : null;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Generate EV spread suggestions.
 *
 * @param {number} genNum - Generation number (1–9)
 * @param {object} myPokemon - { showdownName, baseStats }
 * @param {object} myConfig  - { nature, item, ability, teraType, isDynamaxed }
 * @param {Array}  conditions - Array of condition objects (see QuickCalc.js for shape)
 *
 * @returns {{ conditionSummary: object[] }}
 */
export function generateSpreads(genNum, myPokemon, myConfig, conditions) {
  const gen = getGen(genNum);
  const { showdownName, baseStats } = myPokemon;

  const zeroEvs = { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 };
  // Start from existing EVs so the optimizer doesn't re-search stats already invested
  let currentEvs = { ...zeroEvs, ...(myConfig.evs || {}) };
  const conditionSummary = [];

  // ── Step 1: find minimums per condition in priority order ──────────────
  for (const cond of conditions) {
    if (!cond.moveName || !cond.targetShowdownName) {
      conditionSummary.push({ condition: cond, status: 'incomplete' });
      continue;
    }

    if (cond.type === 'ko') {
      const result = findMinKoEvs(gen, showdownName, baseStats, myConfig, currentEvs, cond);
      if (!result) {
        conditionSummary.push({ condition: cond, status: 'impossible' });
      } else {
        currentEvs[result.stat] = Math.max(currentEvs[result.stat], result.evs);
        conditionSummary.push({ condition: cond, status: 'met', minEvs: result });
      }
    } else { // survive
      const result = findMinSurviveEvs(gen, showdownName, baseStats, myConfig, currentEvs, cond);
      if (!result) {
        conditionSummary.push({ condition: cond, status: 'impossible' });
      } else {
        currentEvs.hp = Math.max(currentEvs.hp, result.hp);
        currentEvs[result.defStat] = Math.max(currentEvs[result.defStat], result.defEvs);
        conditionSummary.push({ condition: cond, status: 'met', minEvs: result });
      }
    }
  }

  // ── Step 2: cap each stat at 252 ──────────────────────────────────────
  for (const k of Object.keys(currentEvs)) {
    currentEvs[k] = Math.min(currentEvs[k], EV_MAX);
  }

  return { conditionSummary };
}
